// Đại lý duyệt yêu cầu nâng cấp của cấp dưới (docs/KE_HOACH_NANG_CAP_DON_VI.md §3.7): Shop trong nhánh đạt chỉ tiêu xin
// lên CTV của mình. Duyệt = chọn bảng giá CTV + bảng hoa hồng trong thư viện của mình. Nhà phân phối duyệt thay được
// bất cứ lúc nào (khi đó bạn được báo).

import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { get, post } from '@/lib/api'
import { money } from '@/lib/format'
import { Button } from '@/components/ui/button'
import { PageHeader } from '@/components/ui/page-header'
import { Section } from '@/components/ui/section'
import { LoadError } from '@/components/ui/data-notice'
import { IndexBadge } from '@/components/ui/data-table'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { errorMessage } from '@/components/price-lists/types'
import { TO_LABEL, TYPE_LABEL, monthLabel, type UpgradeRequest } from '@/components/upgrade/types'

const STATUS: Record<UpgradeRequest['status'], string> = { pending: 'Chờ duyệt', approved: 'Đã duyệt', rejected: 'Đã từ chối', cancelled: 'Đơn vị hủy' }

export default function UpgradeRequestsPage() {
  const [all, setAll] = useState(false)
  const [approving, setApproving] = useState<UpgradeRequest | null>(null)
  const [rejecting, setRejecting] = useState<UpgradeRequest | null>(null)
  const q = useQuery({ queryKey: ['upgrade', 'approvals', all], queryFn: () => get<{ requests: UpgradeRequest[] }>(`/upgrade/approvals?status=${all ? 'all' : 'pending'}`) })
  const rows = q.data?.requests ?? []
  return (
    <div className="mx-auto max-w-4xl space-y-5">
      <PageHeader title="Duyệt nâng cấp" actions={<Link to="/network" className="text-sm text-accent hover:underline">← Hệ thống</Link>} />
      <label className="flex items-center gap-2 text-sm text-inkSoft">
        <input type="checkbox" checked={all} onChange={(e) => setAll(e.target.checked)} className="accent-accent" /> Gồm cả yêu cầu đã xử lý
      </label>
      {q.isError ? (
        <LoadError onRetry={() => q.refetch()} />
      ) : q.isLoading ? (
        <p className="p-8 text-center text-sm text-muted">Đang tải…</p>
      ) : rows.length === 0 ? (
        <Section>
          <p className="p-4 text-sm text-muted">{all ? 'Chưa có yêu cầu nào.' : 'Không có yêu cầu chờ duyệt.'}</p>
        </Section>
      ) : (
        rows.map((r, i) => {
          const last = r.metrics?.months?.[r.metrics.months.length - 1]
          return (
            <Section key={r.id}>
              <div className="flex flex-wrap items-start gap-3 p-4 text-sm">
                <div className="min-w-0 flex-1 space-y-1">
                  <p className="flex items-start gap-2 font-medium text-ink">
                    <IndexBadge n={i + 1} />
                    <span className="min-w-0 flex-1">
                      {r.unit?.name} · {TYPE_LABEL[r.data.fromType]} → {TO_LABEL[r.data.toType]}
                    </span>
                  </p>
                  {last && (
                    <p className="text-xs text-inkSoft">
                      Tháng {monthLabel(last.month)}: {last.deliveredOrders.toLocaleString('vi-VN')} đơn giao thành công · {money(last.goodsValue)} tiền hàng
                    </p>
                  )}
                  {r.data.leaves.length > 0 && <p className="text-xs text-inkSoft">Rời {r.data.leaves.map((l) => `${TYPE_LABEL[l.type] ?? l.type} ${l.name}`).join(', ')}, về dưới bạn.</p>}
                  {r.message && <p className="text-xs text-ink">“{r.message}”</p>}
                  <p className="text-xs text-muted">
                    Gửi {new Date(r.createdAt).toLocaleDateString('vi-VN')} · {STATUS[r.status]}
                    {r.status === 'pending' && r.waitingDays > 0 ? ` · chờ ${r.waitingDays} ngày` : ''}
                    {r.reviewNote ? ` · ${r.reviewNote}` : ''}
                  </p>
                </div>
                {r.status === 'pending' && (
                  <div className="flex gap-2">
                    <Button size="sm" onClick={() => setApproving(r)}>Duyệt</Button>
                    <Button size="sm" variant="outline" onClick={() => setRejecting(r)}>Từ chối</Button>
                  </div>
                )}
              </div>
            </Section>
          )
        })
      )}
      {approving && <ApproveDialog req={approving} onClose={() => setApproving(null)} />}
      {rejecting && <RejectDialog req={rejecting} onClose={() => setRejecting(null)} />}
    </div>
  )
}

interface Options {
  priceLists: { id: string; code: string; name: string; unitCount: number }[]
  schemes: { id: string; code: string; name: string }[]
}

function ApproveDialog({ req, onClose }: { req: UpgradeRequest; onClose: () => void }) {
  const qc = useQueryClient()
  const [priceListId, setPriceListId] = useState('')
  const [schemeId, setSchemeId] = useState('')
  const opts = useQuery({ queryKey: ['upgrade', 'approval-options', req.id], queryFn: () => get<Options>(`/upgrade/approvals/${req.id}/options`), retry: false })
  const approve = useMutation({
    mutationFn: () => post(`/upgrade/approvals/${req.id}/approve`, { priceListId, schemeId }),
    onSuccess: () => {
      toast.success(`${req.unit?.name} đã thành CTV của bạn`)
      qc.invalidateQueries({ queryKey: ['upgrade'] })
      qc.invalidateQueries({ queryKey: ['network-units'] })
      onClose()
    },
    onError: (e) => toast.error(errorMessage(e, 'Không duyệt được')),
  })
  const lists = opts.data?.priceLists ?? []
  const schemes = opts.data?.schemes ?? []
  return (
    <Dialog open onOpenChange={(o) => !o && !approve.isPending && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader className="pr-8">
          <DialogTitle>Duyệt {req.unit?.name} lên CTV</DialogTitle>
          <DialogDescription>Đơn đã tạo giữ nguyên giá cũ.</DialogDescription>
        </DialogHeader>
        {opts.isLoading ? (
          <p className="text-sm text-muted">Đang tải thư viện…</p>
        ) : opts.isError ? (
          <p className="text-sm text-danger">{errorMessage(opts.error, 'Không tải được thư viện')}</p>
        ) : (
          <div className="space-y-3 text-sm">
            <select value={priceListId} onChange={(e) => setPriceListId(e.target.value)} className="h-10 w-full rounded-lg border border-rule bg-paper px-3">
              <option value="">— Bảng giá CTV —</option>
              {lists.map((l) => <option key={l.id} value={l.id}>{l.name} ({l.code})</option>)}
            </select>
            {lists.length === 0 && <p className="text-xs text-warn">Chưa có bảng giá CTV.</p>}
            <select value={schemeId} onChange={(e) => setSchemeId(e.target.value)} className="h-10 w-full rounded-lg border border-rule bg-paper px-3">
              <option value="">— Bảng chiết khấu —</option>
              {schemes.map((x) => <option key={x.id} value={x.id}>{x.name} ({x.code})</option>)}
            </select>
            {schemes.length === 0 && <p className="text-xs text-warn">Chưa có bảng chiết khấu.</p>}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={approve.isPending}>Hủy</Button>
          <Button onClick={() => approve.mutate()} disabled={!priceListId || !schemeId || approve.isPending}>{approve.isPending ? 'Đang duyệt…' : 'Duyệt'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function RejectDialog({ req, onClose }: { req: UpgradeRequest; onClose: () => void }) {
  const qc = useQueryClient()
  const [reason, setReason] = useState('')
  const reject = useMutation({
    mutationFn: () => post(`/upgrade/approvals/${req.id}/reject`, { reason: reason.trim() }),
    onSuccess: () => {
      toast.success('Đã từ chối')
      qc.invalidateQueries({ queryKey: ['upgrade'] })
      onClose()
    },
    onError: (e) => toast.error(errorMessage(e, 'Không từ chối được')),
  })
  return (
    <Dialog open onOpenChange={(o) => !o && !reject.isPending && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader className="pr-8">
          <DialogTitle>Từ chối yêu cầu của {req.unit?.name}</DialogTitle>
        </DialogHeader>
        <textarea value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} rows={3} placeholder="Lý do *" className="w-full rounded-lg border border-rule bg-paper px-3 py-2 text-sm" />
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={reject.isPending}>Hủy</Button>
          <Button variant="destructive" onClick={() => reject.mutate()} disabled={reason.trim().length < 3 || reject.isPending}>Từ chối</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
